import env from '../env.json'
import { store } from 'app/store'
import { LOGOUT, REFRESH_TOKEN_SUCCESS } from 'store/auth/AuthConstants'

/**
 * Outcome of a session refresh attempt.
 * - `refreshed`       — new token issued; the original request can be retried.
 * - `auth-rejected`   — the session is genuinely dead (400 TokenNotValid/CredentialsNotValid,
 *                       or 403); LOGOUT dispatched exactly once.
 * - `transient-failed`— network/5xx blip; session left intact, caller should NOT log out.
 * - `no-session`      — there was no token to refresh (already logged out); NOT a rejection.
 */
export type RefreshOutcome = 'refreshed' | 'auth-rejected' | 'transient-failed' | 'no-session'

const REFRESH_PATH = 'api/v1/auth/refreshtoken'
const MAX_ATTEMPTS = 3
const BASE_BACKOFF_MS = 400

/** Kill-switch flag (default off). When off, callers keep their legacy refresh behavior. */
export function isUnifiedRefreshEnabled(): boolean {
  return store.getState()?.flags?.flags?.unified_token_refresh === true
}

/**
 * The refresh endpoint does NOT use 401 to report a dead session — it uses 400 with
 * an `ErrorCode` body. Verified live against QA on 2026-08-05:
 *
 *   POST api/v1/auth/refreshtoken {"AccessToken":"<unusable>"}
 *   → HTTP 400  {"ErrorCode":"TokenNotValid"}
 *
 * (PascalCase because Providers.Api sets `JsonSerializerOptions.PropertyNamingPolicy = null`.)
 *
 * Backend mapping — AuthErrorCodeCollection.cs + AuthErrorCodeHelper.cs:
 *   TokenNotValid        400  revoked/expired/unparseable session   → dead
 *   CredentialsNotValid  400  account not found                     → dead
 *   AccountLocked        400  AccountLocked *or TooManyRequests*    → NOT dead, back off
 *   ActionBlocked        403                                        → dead
 *
 * `AccountLocked` is deliberately excluded: `MapDefault` folds Stytch's
 * `TooManyRequestsException` into it, and on the session-authenticate path that is
 * overwhelmingly rate-limiting. Ending a visit over a 429 is the bug we're fixing.
 */
const FATAL_REFRESH_ERROR_CODES = new Set(['TokenNotValid', 'CredentialsNotValid'])

const delay = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })

/** Reads `ErrorCode` case-insensitively so a backend casing change can't silently
 * reintroduce the zombie session (a dead token misread as a transient blip). */
function extractErrorCode(data: unknown): string | null {
  if (typeof data !== 'object' || data === null) return null
  // PascalCase is what Providers.Api emits today; accept camelCase so flipping
  // `PropertyNamingPolicy` can't silently strand providers in a zombie session.
  if ('ErrorCode' in data && typeof data.ErrorCode === 'string') return data.ErrorCode
  if ('errorCode' in data && typeof data.errorCode === 'string') return data.errorCode
  return null
}

/**
 * Does a *failed* refresh mean the session is genuinely dead, or was it a blip?
 *
 * Shared by the two legacy refresh paths (`initOrvalTokenModules` and
 * `restclient.refreshTokens`), which previously logged the provider out on ANY
 * failure — 5,710 `orval-refresh-failed` + 706 `restclient-refresh-failed` Sentry
 * events. Both now log out only when this returns true.
 *
 * Error shapes it has to cope with:
 *  - frontend-utils `buildAppError` (Orval): `{ status, code, details: <parsed body>, … }`.
 *    Note `code` is read upstream from `payload.code` (lowercase) while Providers.Api
 *    emits PascalCase `ErrorCode` — so for our backend `code` is `undefined` and the
 *    value we need is inside `details`. Reading only `code` here would silently
 *    classify every dead session as transient.
 *  - `apiCall` rejection (restclient): `{ ...parsedBody, status, message }` — `ErrorCode`
 *    sits at the top level.
 *  - Native network failures: `TypeError('Failed to fetch' | 'Load failed')` — no status.
 *
 * Fail-open by design: anything we cannot positively identify as an auth rejection is
 * treated as transient. A provider stranded mid-visit is worse than one extra request
 * against an already-dead session, and the next 401 ends it properly within the 300s
 * token lifetime.
 */
export function isFatalRefreshError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  const status = 'status' in error && typeof error.status === 'number' ? error.status : null
  if (status === 401 || status === 403) return true
  // Top level first (restclient shape), then `details` (buildAppError shape).
  const code = extractErrorCode(error) ?? extractErrorCode('details' in error ? error.details : null)
  return code !== null && FATAL_REFRESH_ERROR_CODES.has(code)
}

/**
 * Human-readable description of a refresh failure for the Sentry logout context.
 * `buildAppError` and `apiCall` both reject with plain objects, not Errors, so
 * `String(e)` yields "[object Object]" — which is exactly what the existing
 * `orval-refresh-failed` events recorded and why they were undiagnosable.
 */
export function describeRefreshError(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'object' && error !== null) {
    const status = 'status' in error ? error.status : undefined
    const message = 'message' in error && typeof error.message === 'string' ? error.message : undefined
    const code = extractErrorCode(error) ?? extractErrorCode('details' in error ? error.details : null)
    const parts = [status !== undefined ? `status=${String(status)}` : null, code ? `code=${code}` : null, message]
    const described = parts.filter(Boolean).join(' ')
    if (described) return described
  }
  return String(error)
}

function extractAccessToken(data: unknown): string | null {
  if (typeof data === 'object' && data !== null && 'AccessToken' in data) {
    const token = data.AccessToken
    return typeof token === 'string' && token.length > 0 ? token : null
  }
  return null
}

/**
 * One refresh HTTP call. Uses a raw `fetch` (NOT the Orval fetcher) on purpose:
 * the Orval fetcher retries 401s by calling the refresh module, which would
 * re-enter this single-flight refresh and deadlock.
 */
async function attemptRefresh(token: string): Promise<{ outcome: RefreshOutcome; retryable: boolean }> {
  try {
    const res = await fetch(`${env.baseURL}${REFRESH_PATH}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        // Match both existing refresh callers (Orval makeRequest + restclient apiCall),
        // which send the current bearer token; the endpoint may rely on it.
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ AccessToken: token }),
    })

    if (res.ok) {
      // `undefined` distinguishes an unparseable body (proxy/server blip) from a
      // body that parsed but carried no token.
      const data: unknown = await res.json().catch(() => undefined)
      if (data === undefined) {
        // A 200 we can't parse is a transient blip, not an auth decision — retry
        // rather than log the provider out on a bad payload.
        return { outcome: 'transient-failed', retryable: true }
      }
      const accessToken = extractAccessToken(data)
      if (accessToken) {
        store.dispatch({ type: REFRESH_TOKEN_SUCCESS, payload: { AccessToken: accessToken } })
        return { outcome: 'refreshed', retryable: false }
      }
      // 200 with a parseable body but no token means the server won't renew this session.
      return { outcome: 'auth-rejected', retryable: false }
    }

    // 403 is ActionBlocked. 401 shouldn't occur on this endpoint, but if the contract
    // ever changes back, honour it rather than stranding the provider.
    if (res.status === 401 || res.status === 403) {
      return { outcome: 'auth-rejected', retryable: false }
    }

    if (res.status === 400) {
      const data: unknown = await res.json().catch(() => undefined)
      const errorCode = extractErrorCode(data)
      if (errorCode !== null && FATAL_REFRESH_ERROR_CODES.has(errorCode)) {
        // The session is genuinely dead — don't hammer it MAX_ATTEMPTS times.
        return { outcome: 'auth-rejected', retryable: false }
      }
      // Rate-limiting is worth backing off for; anything else here is a malformed
      // request from us, which must never end a provider's session.
      return { outcome: 'transient-failed', retryable: errorCode === 'AccountLocked' }
    }

    // Server error → transient, worth retrying. Other 4xx → fail without logging out.
    return { outcome: 'transient-failed', retryable: res.status >= 500 }
  } catch {
    // Network error / timeout → transient (e.g. bandwidth-starved during a video visit).
    return { outcome: 'transient-failed', retryable: true }
  }
}

async function resolveOutcome(): Promise<RefreshOutcome> {
  const token = store.getState()?.auth?.accessToken
  if (!token) {
    // No token to present. This is NOT an auth rejection: the common cause is a
    // request that was already in flight when the session ended, and treating it
    // as a rejection would dispatch a second LOGOUT — which `AuthReducer` turns
    // into another `{...initialState}` reset and `App.tsx` can then re-trigger.
    return 'no-session'
  }

  let outcome: RefreshOutcome = 'transient-failed'
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    const result = await attemptRefresh(token)
    outcome = result.outcome
    if (outcome === 'refreshed' || outcome === 'auth-rejected') {
      break
    }
    if (!result.retryable || attempt === MAX_ATTEMPTS) {
      break
    }
    await delay(BASE_BACKOFF_MS * attempt)
  }
  return outcome
}

async function performRefresh(): Promise<RefreshOutcome> {
  const outcome = await resolveOutcome()
  // Log out ONLY on a real auth rejection — never on a transient network/5xx blip.
  if (outcome === 'auth-rejected') {
    store.dispatch({ type: LOGOUT, reason: 'session-expired' })
  }
  return outcome
}

/**
 * Holds the single in-progress refresh, or `null` when none is running.
 * Lifecycle: `null` → the pending `performRefresh()` promise (set by the first
 * caller) → back to `null` once it settles (via `.finally`), so the *next* 401
 * starts a fresh refresh rather than reusing a resolved one.
 */
let inFlight: Promise<RefreshOutcome> | null = null

/**
 * Single-flight session refresh. Concurrent 401s (e.g. a burst of waiting-room
 * polls hitting the just-expired JWT) all await the SAME refresh, so the token
 * is rotated exactly once — eliminating the race where a second refresh runs
 * with a stale token and forces a spurious logout.
 */
export function refreshSession(): Promise<RefreshOutcome> {
  // First caller kicks off the refresh; everyone who arrives while it's still
  // pending gets the same promise. `.finally` clears the slot so a later 401
  // (after this one settles) can start a new refresh.
  if (!inFlight) {
    inFlight = performRefresh().finally(() => {
      inFlight = null
    })
  }
  return inFlight
}
